/* eslint-disable consistent-return */
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';

import checkStandardConditions from './check-standard-conditions';
import checkTotalQuantity from './check-total-quantity';
import checkTotalWeight from './check-total-weight';

import Condition from './condition';
import checkTotalSpend from './check-total-spend';
import { getSpecialSettings, orderProductsForDiscountPrecedence } from '../utilities';

// Sort individual conditions equal to itemToConsiderLast's id at the bottom so other conditions will be satisfied first
const sortConditions = (conditions, itemToConsiderLast) => {
  const [topConditions, bottomConditions] = _.reduce(
    conditions || [],
    (lists, currentCondition) => {
      if (currentCondition?.productId !== itemToConsiderLast?.id) {
        lists[0].push(currentCondition);
        return lists;
      }
      lists[1][currentCondition?.weight !== itemToConsiderLast?.weight ? `unshift` : `push`](currentCondition);
      return lists;
    },
    [[], []]
  );
  return [...topConditions, ...bottomConditions];
};

class ConditionsManager {
  constructor(bogoCalcsData, bogoSpecial, items, globalSpecialsSettings) {
    this.bogoCalcsData = bogoCalcsData;
    this.bogoSpecial = bogoSpecial;
    this.conditions = _.map(
      bogoSpecial?.bogoConditions,
      (condition) => new Condition(condition, bogoCalcsData, bogoSpecial, items)
    );
    this.logicOperator = bogoSpecial.bogoConditionLogicOperator ?? 'and';
    this.items = items;
    this.itemsForAPrice = bogoSpecial.itemsForAPrice ?? { enabled: false };
    this.discountToCart = bogoSpecial.discountToCart ?? { enabled: false };
    this.previous = { satisfied: false, satisfiers: {} };
    this.satisfied = false;
    this.satisfiers = {};

    // prefer the specials discount settings before falling back to global settings
    this.specialsSettings = getSpecialSettings(bogoSpecial, globalSpecialsSettings);
    this.totalQuantity = bogoSpecial.totalQuantity ?? { enabled: false };
    this.totalWeight = bogoSpecial.totalWeight ?? { enabled: false };
    this.totalSpend = bogoSpecial.totalSpend ?? { enabled: false };

    this.evaluate({});
  }

  evaluate({ itemToConsiderLast, pendingRewardsSatisfiers, itemsOverride }) {
    this.previous = { satisfied: this.satisfied, satisfiers: this.satisfiers };
    const items = itemsOverride ?? this.items;
    if (itemsOverride) {
      // if there is an itemsOverride we want to then make sure those items are also out of the conditions eligible items list for the adv. conditions
      this.reevaluateConditionsEligibleItems({ items });
    }
    let satisfied;
    let satisfiers;

    const conditions =
      itemToConsiderLast && !this.itemsForAPrice?.enabled
        ? sortConditions(this.conditions, itemToConsiderLast)
        : this.conditions;

    if (this.totalSpend?.enabled) {
      ({ satisfied, satisfiers } = checkTotalSpend({
        bogoCalcsData: this.bogoCalcsData,
        conditions,
        items,
        itemToConsiderLast,
        itemsForAPrice: this.itemsForAPrice,
        logicOperator: this.logicOperator,
        pendingRewardsSatisfiers,
        specialId: this.bogoSpecial.specialId,
        specialsSettings: this.specialsSettings,
        totalQuantity: this.totalQuantity,
        totalSpend: this.totalSpend,
      }));
    } else if (this.totalWeight?.enabled) {
      ({ satisfied, satisfiers } = checkTotalWeight({
        bogoCalcsData: this.bogoCalcsData,
        conditions,
        items,
        itemsForAPrice: this.itemsForAPrice,
        itemToConsiderLast,
        logicOperator: this.logicOperator,
        pendingRewardsSatisfiers,
        specialId: this.bogoSpecial.specialId,
        specialsSettings: this.specialsSettings,
        totalQuantity: this.totalQuantity,
        totalWeight: this.totalWeight,
      }));
    } else if (this.totalQuantity?.enabled) {
      ({ satisfied, satisfiers } = checkTotalQuantity({
        bogoCalcsData: this.bogoCalcsData,
        conditions,
        items,
        itemsForAPrice: this.itemsForAPrice,
        itemToConsiderLast,
        logicOperator: this.logicOperator,
        pendingRewardsSatisfiers,
        specialId: this.bogoSpecial.specialId,
        specialsSettings: this.specialsSettings,
        totalQuantity: this.totalQuantity,
      }));
    } else {
      ({ satisfied, satisfiers } = checkStandardConditions({
        conditions,
        continueCheckingConditionsAfterSatisfied: this.itemsForAPrice.enabled,
        itemToConsiderLast,
        logicOperator: this.logicOperator,
        pendingRewardsSatisfiers,
        items,
        itemsForAPrice: this.itemsForAPrice,
        specialsSettings: this.specialsSettings,
      }));
    }

    this.satisfied = satisfied;
    this.satisfiers = satisfied ? satisfiers : {};
    return { satisfied: this.satisfied, satisfiers: this.satisfiers };
  }

  revert() {
    const newPrevious = { satisfied: this.satisfied, satisfiers: this.satisfiers };
    this.satisfied = this.previous.satisfied;
    this.satisfiers = this.previous.satisfiers;
    this.previous = newPrevious;
    _.forEach(this.conditions, (condition) => condition.revert());
  }

  reevaluateConditionsEligibleItems({ items }) {
    _.forEach(this.conditions, (condition) => {
      condition.eligibleItems = orderProductsForDiscountPrecedence(
        _.intersectionBy(condition.eligibleItems, items, 'key'),
        this.specialsSettings,
        this.itemsForAPrice.enabled ? 'rewards' : 'conditions'
      );
    });
  }
}

export default ConditionsManager;
